import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import type { NavLinkProps } from 'react-router'

import { Icon, type IconName } from '@fv/client-components'
import { authUri } from '@fv/client-core'

import { AdminNavLink } from '../shared/AdminLink'
import { ThemeSelector } from './ThemeSelector'

export const Nav = () => {
  return (
    <nav className="flex w-full items-center bg-base-300 text-base-content p-0">
      <NavItem to="/" end icon="home">
        Admin Portal
      </NavItem>
      <NavItem to="/dispatches" icon="list-ul">
        Dispatch List
      </NavItem>
      <NavItem to="/loads" icon="truck">
        Loads
      </NavItem>
      <NavItem to="/carriers" icon="truck-ramp-box">
        Carriers
      </NavItem>
      <NavItem to="/accounts" icon="building">
        Accounts
      </NavItem>
      <NavItem to="/provisioning" icon="boxes">
        Provisioning
      </NavItem>
      <NavItem to="/vendors" icon="store-alt">
        Vendors
      </NavItem>
      <NavItem to="/reports" icon="analytics">
        Reports
      </NavItem>
      <NavItem to="/tools" icon="hammer">
        Tools
      </NavItem>
      <div className="ml-auto mr-3 flex gap-4 items-center">
        <div>
          <ThemeSelector />
        </div>
        <div>
          <a className="text-sm" href={`${authUri}/signout`}>
            <Icon icon="power-off" /> Logout
          </a>
        </div>
      </div>
    </nav>
  )
}

type ItemProps = PropsWithChildren<
  Pick<NavLinkProps, 'end' | 'to'> & {
    icon: IconName
  }
>

export const NavItem = ({ icon, children, ...props }: ItemProps) => {
  return (
    <AdminNavLink
      {...props}
      className={({ isActive }) =>
        clsx(
          'inline-flex items-center !no-underline p-4 text-sm border-b-2 border-transparent hover:bg-neutral-content/10',
          {
            'border-b-primary': isActive,
            'text-base-content': !isActive,
          },
        )
      }
      icon={icon}
    >
      {children}
    </AdminNavLink>
  )
}

type SubNavProps = PropsWithChildren<{ className?: string }>

export const SubNav = ({ children, className }: SubNavProps) => {
  return (
    <nav
      className={clsx(
        'flex w-full items-stretch bg-base-200 text-base-content p-0 border-t border-base-content/30',
        className,
      )}
    >
      {children}
      <div
        aria-hidden
        className="min-h-0 min-w-0 flex-1 border-b-2 border-b-base-content/30"
      />
    </nav>
  )
}

type SubNavItemProps = PropsWithChildren<Pick<NavLinkProps, 'end' | 'to'>>

export const SubNavItem = ({ children, ...props }: SubNavItemProps) => {
  return (
    <AdminNavLink
      {...props}
      className={({ isActive }) =>
        clsx(
          'flex shrink-0 items-center self-stretch !no-underline px-4 py-2.5 text-sm border-r border-r-base-content/20 text-base-content border-b-2',
          isActive ? 'border-b-secondary' : 'border-b-base-content/30',
        )
      }
    >
      {children}
    </AdminNavLink>
  )
}
